import { SortBy } from '@cognitivecredit/ui-core'
import dayjs from 'dayjs'
import { File as CcFile, medDate, TreeNode } from '@cognitivecredit/ui-utils'
import { DealConnectFile, DealConnectFolderNote } from '@/components/dealconnect/types'

type DealWithDate = {
  dateUpdated?: string | null
  createdAt?: string | null
}

type DateFieldName = keyof DealWithDate
type DateSortBy = (Omit<NonNullable<SortBy>, 'fieldName'> & { fieldName: DateFieldName }) | null

export const sortedByDateUpdated =
  <T extends DealWithDate>(sortBy: DateSortBy) =>
  (a: T, b: T) => {
    if (!sortBy) {
      return 0
    }
    const dateA = dayjs(a[sortBy.fieldName])
    const dateB = dayjs(b[sortBy.fieldName])
    const isValidA = dateA.isValid()
    const isValidB = dateB.isValid()
    if (!isValidA && !isValidB) {
      return 0
    }
    if (!isValidA) {
      return 1
    }
    if (!isValidB) {
      return -1
    }
    if (dateA.isSame(dateB)) {
      return 0
    }
    if (sortBy.direction === 'ASC') {
      return dateA.isAfter(dateB) ? 1 : -1
    }
    return dateA.isBefore(dateB) ? 1 : -1
  }

const SYNDTRAK_SEPARATOR = /\s*>>\s*/

// SyndTrak stores a folder address as one segment, "Root >> Communications >> Sub".
// The explorer splits on "/", so turn that address into nested folders and drop the
// SyndTrak "Root" container. The original filePath stays unchanged for downloads.
export const syndtrakFolderPath = (path: string): string => {
  const trimmed = path.trim()
  if (!trimmed.includes('>>')) {
    return trimmed === 'Root' ? '' : trimmed
  }
  const segments = trimmed
    .split('/')
    .flatMap((segment) => segment.split(SYNDTRAK_SEPARATOR))
    .map((segment) => segment.trim())
    .filter(Boolean)
  if (segments[0] === 'Root') {
    segments.shift()
  }
  return segments.join('/')
}

export const syndtrakDisplayPath = (filePath: string, datasite: string): string => {
  if (datasite.toLowerCase() !== 'syndtrak' || !filePath.includes('>>')) {
    return filePath
  }
  const slashIndex = filePath.lastIndexOf('/')
  if (slashIndex === -1) {
    const segments = filePath
      .split(SYNDTRAK_SEPARATOR)
      .map((segment) => segment.trim())
      .filter(Boolean)
    const fileName = segments.pop() ?? filePath
    const folders = syndtrakFolderPath(segments.join(' >> '))
    return folders ? `${folders}/${fileName}` : fileName
  }
  const folders = syndtrakFolderPath(filePath.slice(0, slashIndex))
  const fileName = filePath.slice(slashIndex + 1)
  return folders ? `${folders}/${fileName}` : fileName
}

const folderNoteText = (notes: DealConnectFolderNote[], dealId: string, path: string) => {
  const text = notes.find((item) => item.dealId === dealId && item.path === path)?.note.trim()
  return text || null
}

const dealIdsInNode = (node: TreeNode<DealConnectFile>) => {
  const dealIds: string[] = []
  const visit = (current: TreeNode<DealConnectFile>) => {
    const dealId = current.data.dealSiteId
    if (dealId && !dealIds.includes(dealId)) {
      dealIds.push(dealId)
    }
    current.children?.forEach(visit)
  }
  visit(node)
  return dealIds
}

export const generateDealConnectHierarchy = <T extends CcFile>(
  child: T,
  createFolderData?: (path: string, name: string, child: T, isRoot: boolean) => T,
): TreeNode<T> => {
  const folders = child.path.split('/').slice(0, -1)
  let rootNode: TreeNode<T> = { data: child, children: [], expanded: false }

  // Default folder data creator that handles both File and DealDocument.
  // Folder notes are resolved when the row renders. See noteForExplorerNode.
  const defaultCreateFolderData = (path: string, name: string, childFile: T): T => {
    return {
      ...childFile,
      path,
      name,
      filePath: '',
      modifiedAt: childFile.modifiedAt,
      type: 'folder',
      note: null,
    } as T
  }

  const folderCreator = createFolderData || defaultCreateFolderData

  folders.forEach((folder, index) => {
    const parentId = folders.slice(0, index + 1).join('/')
    let parsedFolderName = folder
    try {
      parsedFolderName = decodeURIComponent(folder)
    } catch {
      console.warn(`Failed to decode folder name: ${folder}. Using raw value.`)
    }
    const data = folderCreator(parentId, parsedFolderName, child, index === 0)
    const node: TreeNode<T> = {
      data,
      children: [],
      expanded: false,
    }
    if (index === 0) {
      rootNode = node
    } else {
      let currentNode = rootNode
      let i = 0
      while (i < index - 1) {
        currentNode = currentNode.children[0]
        i++
      }
      currentNode.children.push(node)
    }
  })

  return rootNode
}

export const dealConnectFileColumns = [
  { key: 'name', text: 'File Name', sortable: true, fieldName: 'name', width: 'minmax(min-content, 1fr)' },
  { key: 'dateModified', text: 'Date Updated', sortable: true, fieldName: 'modifiedAt', width: '10rem' },
  { key: 'lastAccessed', text: 'Last Accessed', sortable: true, fieldName: 'lastAccessedAt', width: '10rem' },
  { key: 'privateDocument', text: 'Document Status', width: '7rem' },
  { key: 'note', text: 'Note', width: '5rem' },
]

const accessedTime = (value?: string | null) => {
  const parsed = value ? Date.parse(value) : Number.NaN
  return Number.isNaN(parsed) ? null : parsed
}

export const latestAccessedAt = (node: TreeNode<DealConnectFile>): string | null => {
  const candidates = [node.data.lastAccessedAt || null]
  if (node.data.type === 'folder') {
    node.children?.forEach((child) => candidates.push(latestAccessedAt(child)))
  }
  return candidates.reduce<string | null>((latest, value) => {
    const valueTime = accessedTime(value)
    if (valueTime == null) {
      return latest
    }
    const latestTime = accessedTime(latest)
    return latestTime == null || valueTime > latestTime ? value : latest
  }, null)
}

export const lastAccessedLabel = (node: TreeNode<DealConnectFile>) => {
  const accessedAt = latestAccessedAt(node)
  return accessedAt ? medDate(accessedAt) : '—'
}

// The explorer merges folders that share a path and keeps the first folder's data.
// Walk every deal in the merged row and use the first note for this path.
export const noteForExplorerNode = (
  node: TreeNode<DealConnectFile>,
  folderNotes: DealConnectFolderNote[] = [],
): string | null => {
  if (node.data.type !== 'folder') {
    return node.data.note?.trim() || null
  }
  if (!folderNotes.length || !node.data.path) {
    return null
  }
  for (const dealId of dealIdsInNode(node)) {
    const text = folderNoteText(folderNotes, dealId, node.data.path)
    if (text) {
      return text
    }
  }
  return null
}
